// components/hauler/JobCard.js
// Hauler-facing job summary card. Bucket A+B visual contract.
//
// Renders payout (green, primary), duration + hourly estimate, tier,
// staging summary, address, optional Quick badge, and an expandable
// payout breakdown sourced from useJobEconomics.
//
// LEU numerics are never rendered (safety invariant 8). Tier name only.

import React, { useState } from "react";
import { useJobEconomics } from "../../hooks/useJobEconomics";
import { canViewerSeeExactLocation } from "../../utils/haulerMapPrivacy";
import { getHaulerPayDisclosure, haulerScopeLabel } from "../../utils/haulerPayDisclosure";
import StatusPill from "./StatusPill";

const COLORS = {
  card: "var(--hl-card)",
  border: "var(--hl-border)",
  text: "var(--hl-text)",
  sub: "var(--hl-text-sub)",
  muted: "var(--hl-text-muted)",
  green: "var(--hl-green)",
  amber: "var(--hl-amber)",
  sky: "var(--hl-sky)",
  red: "var(--hl-red)",
};

// Inline claim error mapping mirrors JobDetailScreen.CLAIM_REASON_COPY so
// the Hauler sees the same message regardless of where the claim fired.
const PROTECTED_CLAIM_REASONS = new Set([
  "tier_not_allowed",
  "exceeds_rig_capacity",
  "trailer_suppressed",
  "missing_crew_names",
  "vehicle_ineligible",
]);

const GENERIC_CLAIM_REJECTION = "This job cannot be claimed right now. Choose another available offer or try again later.";

const CLAIM_REASON_COPY = {
  missing_driver_profile: "Your hauler profile is incomplete.",
  driver_not_approved: "Your hauler profile is not approved yet.",
  driver_unavailable: "Turn availability on before claiming jobs.",
  base_access_required: "Base access is required for this job.",
  unstarted_claim_limit: "Finish, start, or release your current claimed job before claiming another.",
};

function claimReasonCopy(reason) {
  if (PROTECTED_CLAIM_REASONS.has(reason)) return GENERIC_CLAIM_REJECTION;
  return CLAIM_REASON_COPY[reason] || "Can't claim this job right now.";
}

function fmt$(n) {
  if (n == null || isNaN(n)) return "$0.00";
  const v = Number(n);
  return "$" + v.toFixed(2);
}

function shortAddr(job) {
  const a = job?.addr || "";
  if (!a) return "Address pending";
  // Display first comma-segment to keep card tight.
  return a.split(",")[0];
}

function placementSummary(job) {
  return job?.staging_location || job?.placement || "Driveway or outdoor placement area";
}

export default function JobCard({
  job,
  onOpen,
  onClaim,
  showStatus = false,
  viewerUserId = null,
  viewerIsAdmin = false,
}) {
  const econ = useJobEconomics(job);
  const [busy, setBusy] = useState(false);
  const [claimError, setClaimError] = useState(null);
  if (!econ) return null;

  const isQuick = job?.quick === true;
  const disclosure = getHaulerPayDisclosure(job, econ);
  const showExact = disclosure.showExactBreakdown;
  const scopeLabel = haulerScopeLabel(job);

  // Inline action visibility — pool jobs only. Per IC-compliance,
  // assigned-but-not-yet-claimed states have no inline UI; the row's
  // own assigned_driver must remain null until a Hauler self-selects.
  const canClaimInline = !!onClaim
    && job?.status === "available"
    && (job?.assigned_driver == null);

  // Privacy boundary. Available/unclaimed cards hide the exact address
  // from unrelated viewers. The booking owner, assigned Hauler, and
  // admins keep full visibility because they have a legitimate need.
  // Mirrors the map-side helper in src/utils/haulerMapPrivacy.js.
  const isAvailableUnclaimed = job?.status === "available"
    && (job?.assigned_driver == null);
  const viewerHasExactAccess = canViewerSeeExactLocation(job, viewerUserId, { isAdmin: !!viewerIsAdmin });
  const hideExactAddress = isAvailableUnclaimed && !viewerHasExactAccess;

  async function handleClaimClick(e) {
    e.stopPropagation();
    if (!onClaim || busy) return;
    setBusy(true);
    setClaimError(null);
    try {
      await onClaim(job);
      // Success: parent refetch will remove this row from the available
      // slice and add it to claimed; no local state change needed.
    } catch (err) {
      const msg = err?.message || "";
      if (msg === "JOB_ALREADY_CLAIMED") {
        setClaimError("Already claimed by another hauler.");
      } else if (msg.startsWith("CLAIM_REJECTED:")) {
        const reason = msg.split(":")[1];
        setClaimError(claimReasonCopy(reason));
      } else {
        setClaimError("Claim failed. Try again.");
      }
      setBusy(false);
    }
  }

  return (
    <div
      onClick={() => onOpen && onOpen(job)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && onOpen) onOpen(job); }}
      style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 14,
        padding: "14px 14px 12px",
        marginBottom: 10,
        cursor: onOpen ? "pointer" : "default",
        color: COLORS.text,
        minHeight: 44,
      }}
    >
      {/* Status pill (active section only) */}
      {showStatus && job?.status && (
        <div style={{ marginBottom: 8 }}>
          <StatusPill status={job.status} />
        </div>
      )}

      {/* Scope + Quick badge. Pre-verification hides deterministic tier labels. */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, letterSpacing: 1.5, fontWeight: 700, color: COLORS.sub, textTransform: "uppercase" }}>
            {showExact ? econ.tierLabel : scopeLabel}
          </span>
          {showExact && isQuick && (
            <span style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: 1,
              padding: "2px 7px",
              borderRadius: 999,
              background: "rgba(242,151,39,.15)",
              border: `1px solid ${COLORS.amber}`,
              color: COLORS.amber,
              textTransform: "uppercase",
            }}>
              Quick
            </span>
          )}
        </div>
        <span style={{ fontSize: 11, color: COLORS.muted }}>
          Zone {job?.zone || "—"}
        </span>
      </div>

      {/* Pay primary */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: COLORS.sub }}>{disclosure.label}</span>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 28, fontWeight: 700, color: COLORS.green, letterSpacing: -0.5 }}>
          {disclosure.payText}
        </span>
        <span style={{ fontSize: 12, color: COLORS.sub }}>
          {showExact
            ? `~${econ.durationMin} min · ~$${econ.hourly.toFixed(0)}/hr`
            : disclosure.detailText}
        </span>
      </div>

      {/* Address + placement.
          Hidden for unrelated viewers on available/unclaimed cards.
          Owners, the assigned Hauler, and admins see the exact address. */}
      {hideExactAddress ? (
        <>
          <div style={{ fontSize: 14, fontWeight: 500, color: COLORS.text, marginBottom: 2 }}>
            {job?.zone ? `Zone ${job.zone} pickup` : "Boyer Hill pickup"}
          </div>
          <div style={{ fontSize: 12, color: COLORS.sub, marginBottom: 10 }}>
            Exact address shown after claim
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 14, fontWeight: 500, color: COLORS.text, marginBottom: 2 }}>
            {shortAddr(job)}
          </div>
          <div style={{ fontSize: 12, color: COLORS.sub, marginBottom: 10 }}>
            {placementSummary(job)}
          </div>
        </>
      )}

      {/* Inline Claim button — pool jobs only */}
      {canClaimInline && (
        <button
          type="button"
          onClick={handleClaimClick}
          disabled={busy}
          style={{
            width: "100%",
            padding: "12px",
            borderRadius: 10,
            border: "none",
            background: busy ? "rgba(61,144,217,.5)" : COLORS.sky,
            color: "#FFFFFF",
            fontWeight: 800,
            fontSize: 14,
            cursor: busy ? "wait" : "pointer",
            fontFamily: "inherit",
            minHeight: 44,
            marginBottom: claimError ? 6 : 10,
          }}
        >
          {busy ? "Claiming…" : "Claim job"}
        </button>
      )}
      {claimError && (
        <div style={{ fontSize: 12, color: COLORS.red, marginBottom: 10, fontWeight: 600 }} onClick={(e) => e.stopPropagation()}>
          {claimError}
        </div>
      )}

      {/* Expandable breakdown */}
      <details style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 10 }} onClick={(e) => e.stopPropagation()}>
        <summary style={{ fontSize: 12, color: COLORS.sky, cursor: "pointer", listStyle: "none", fontWeight: 600 }}>
          {showExact ? "Payout breakdown" : "Earnings range"}
        </summary>
        {showExact ? (
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
            <Row label="Customer pays" value={fmt$(econ.gross)} />
            <Row label="LoadDash service fee (10%)" value={"−" + fmt$(econ.companyMargin)} />
            {/* Variable costs are paid by the Hauler, not retained by LoadDash. */}
            <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" }}>
              Hauler's potential expenses
            </div>
            <Row label="Payment processing estimate (3.5%)" value={"−" + fmt$(econ.stripeFee)} />
            <Row label="Disposal allowance" value={"−" + fmt$(econ.dumpFee)} />
            <Row label="Fuel allowance" value={"−" + fmt$(econ.fuelEst)} />
            <div style={{ height: 1, background: COLORS.border, margin: "4px 0" }} />
            <Row label="Estimated Hauler payout" value={fmt$(econ.driverPay)} bold />
          </div>
        ) : (
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
            <Row label="Estimated earnings range" value={disclosure.payText} bold />
            <div style={{ fontSize: 12, color: COLORS.sub, lineHeight: 1.45 }}>
              Exact payout and cost breakdown appear after load verification.
            </div>
          </div>
        )}
      </details>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
      <span style={{ color: COLORS.sub }}>{label}</span>
      <span style={{ color: bold ? COLORS.green : COLORS.text, fontWeight: bold ? 700 : 500 }}>{value}</span>
    </div>
  );
}
